import { DecoratorNode, $applyNodeReplacement } from 'lexical';
import { markRaw } from 'vue';
import EditorAttachment from '../Components/EditorAttachment.vue';
import { attachment } from '../Themes/EditorClasses';

const legacyAttachmentClass = 'ql-attachment';

// The download URL is a query-string endpoint (…?file=…&path=…). Content that round-trips through an
// HTML-encoding layer arrives with the separator as &amp;, which the browser then requests literally,
// dropping the `path` param and breaking the download. A Lexical node must hold the real URL, so collapse
// any &amp; back to & at creation. Targeted (not full entity decode) to avoid mangling params that look
// like entities (e.g. &reg=, &copy=). Looped to undo multiple encode passes (&amp;amp; → &).
function normalizeUrl(url) {
    if (!url) return url;
    let result = url;
    while (result.includes('&amp;')) {
        result = result.replace(/&amp;/g, '&');
    }
    return result;
}

function convertAttachmentElement(domNode) {
    const url = domNode.getAttribute('href');
    if (!url) return null;
    return { node: $createAttachmentNode({ url, fileName: domNode.textContent || url }) };
}

export class AttachmentNode extends DecoratorNode {
    __url;
    __fileName;

    static getType() {
        return 'attachment';
    }

    static clone(node) {
        return new AttachmentNode(node.__url, node.__fileName, node.__key);
    }

    constructor(url, fileName, key) {
        super(key);
        this.__url = url;
        this.__fileName = fileName || url;
    }

    static importJSON(serializedNode) {
        const { url, fileName } = serializedNode;
        return $createAttachmentNode({ url, fileName });
    }

    exportJSON() {
        return {
            type: 'attachment',
            version: 1,
            url: this.__url,
            fileName: this.__fileName,
        };
    }

    static importDOM() {
        return {
            a: (domNode) => {
                // Only OUR/legacy attachment links; plain links fall through to LinkNode.
                if (domNode.classList.contains(attachment) || domNode.classList.contains(legacyAttachmentClass)) {
                    return { conversion: convertAttachmentElement, priority: 2 };
                }
                return null;
            },
        };
    }

    exportDOM() {
        const element = document.createElement('a');
        element.setAttribute('href', this.__url);
        element.setAttribute('download', '');
        element.setAttribute('class', attachment);
        // span.__name mirrors EditorAttachment.vue so underline styling matches editor + public render.
        const name = document.createElement('span');
        name.setAttribute('class', `${attachment}__name`);
        name.textContent = this.__fileName;
        element.appendChild(name);
        return { element };
    }

    isInline() {
        return true;
    }

    createDOM() {
        return document.createElement('span');
    }

    updateDOM() {
        return false;
    }

    decorate() {
        return {
            component: markRaw(EditorAttachment),
            props: {
                url: this.__url,
                fileName: this.__fileName,
            },
        };
    }
}

export function $createAttachmentNode({ url, fileName }) {
    return $applyNodeReplacement(new AttachmentNode(normalizeUrl(url), fileName));
}

export function $isAttachmentNode(node) {
    return node instanceof AttachmentNode;
}
